import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import AgentDashboard from './AgentDashboard';
import LoginPage from './components/LoginPage';
import PrivacyPolicyView from './components/PrivacyPolicyView';
import PublicHomePage from './components/PublicHomePage';
import SplashScreen from './components/SplashScreen';
import TermsOfServiceView from './components/TermsOfServiceView';
import * as api from './api/client';
import {
  CONNECTOR_OAUTH_EVENT,
  CONNECTOR_OAUTH_MESSAGE_TYPE,
  broadcastConnectorOAuthResult,
  clearRememberedConnectorOAuthPopup,
  getConnectorOAuthRedirectUri,
  hasConnectorOAuthParams,
  isRememberedConnectorOAuthPopup,
} from './utils/connectorOAuth';

type Screen = 'login' | 'splash' | 'dashboard';

export interface GoogleUser {
  name: string;
  picture?: string;
  email?: string;
}

// ── Session persistence ───────────────────────────────────────────────────────

const SESSION_KEY = 'praxys_session_v1';
const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface StoredSession {
  user: GoogleUser;
  expiresAt: number; // epoch ms
}

function saveSession(user: GoogleUser): void {
  const session: StoredSession = { user, expiresAt: Date.now() + SESSION_TTL_MS };
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch (err) {
    console.warn('[Praxys] Could not persist session to localStorage:', err);
  }
}

function loadSession(): GoogleUser | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session: StoredSession = JSON.parse(raw);
    if (Date.now() > session.expiresAt) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return session.user;
  } catch (err) {
    console.warn('[Praxys] Could not read session from localStorage:', err);
    return null;
  }
}

function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch (err) {
    console.warn('[Praxys] Could not clear session:', err);
  }
}

function getInviteToken(): string {
  try {
    return new URLSearchParams(window.location.search).get('invite') || '';
  } catch {
    return '';
  }
}

// ── JWT helpers ───────────────────────────────────────────────────────────────

function decodeGoogleJwt(credential: string): GoogleUser {
  try {
    const payload = JSON.parse(atob(credential.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return { name: payload.name || payload.email || 'User', picture: payload.picture, email: payload.email };
  } catch {
    return { name: 'User' };
  }
}

// ── Admin user detection ──────────────────────────────────────────────────────

const ADMIN_USERS_RAW = process.env.REACT_APP_ADMIN_USERS || '';
const ADMIN_USERS: string[] = ADMIN_USERS_RAW
  .split(',')
  .map(s => s.trim().toLowerCase())
  .filter(Boolean);

function isAdminUser(user: GoogleUser | null): boolean {
  if (!user || ADMIN_USERS.length === 0) return false;
  const emailPrefix = (user.email || '').split('@')[0].toLowerCase();
  const nameLower = (user.name || '').toLowerCase().replace(/\s+/g, '');
  const emailLower = (user.email || '').toLowerCase();
  return ADMIN_USERS.some(
    admin => admin === emailPrefix || admin === nameLower || admin === emailLower,
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

const App: React.FC = () => {
  // Restore a previously saved session so the user isn't prompted to log in
  // again after every page refresh.  The stored session expires after 7 days.
  const restoredUser = loadSession();

  const [screen, setScreen] = useState<Screen>(restoredUser ? 'dashboard' : 'login');
  // Decode display name and picture from JWT for UI only.
  // Authentication is validated by Google's OAuth flow; this payload is
  // not trusted for access-control decisions.
  const [user, setUser] = useState<GoogleUser | null>(restoredUser);
  const [authError, setAuthError] = useState<string>('');
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [handlingConnectorOAuth, setHandlingConnectorOAuth] = useState(
    () => hasConnectorOAuthParams(window.location.search),
  );

  useEffect(() => {
    document.body.setAttribute('data-theme', 'dark');
  }, []);

  useEffect(() => {
    const handleConnectorOAuthMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== CONNECTOR_OAUTH_MESSAGE_TYPE) return;
      if (event.data?.payload) {
        window.dispatchEvent(new CustomEvent(CONNECTOR_OAUTH_EVENT, { detail: event.data.payload }));
      }
    };

    window.addEventListener('message', handleConnectorOAuthMessage);
    return () => window.removeEventListener('message', handleConnectorOAuthMessage);
  }, []);

  useEffect(() => {
    if (!hasConnectorOAuthParams(window.location.search)) return undefined;

    const searchParams = new URLSearchParams(window.location.search);
    const redirectUri = getConnectorOAuthRedirectUri();
    let cancelled = false;
    let handledInPopup = false;

    const clearUrl = () => {
      const nextUrl = `${window.location.pathname}${window.location.hash}`;
      window.history.replaceState({}, document.title, nextUrl);
    };

    setHandlingConnectorOAuth(true);

    void api.completeConnectorOAuth({
      state: searchParams.get('state') || '',
      code: searchParams.get('code') || undefined,
      installation_id: searchParams.get('installation_id') || undefined,
      error: searchParams.get('error') || undefined,
      error_description: searchParams.get('error_description') || undefined,
      redirect_uri: redirectUri,
    }).then((response) => {
      if (cancelled) return;
      const isPopup =
        isRememberedConnectorOAuthPopup(window.location.search) ||
        (window.opener && window.opener !== window) ||
        window.name.endsWith('_oauth');
      clearUrl();
      window.dispatchEvent(new CustomEvent(CONNECTOR_OAUTH_EVENT, { detail: response.data }));
      broadcastConnectorOAuthResult(response.data);
      clearRememberedConnectorOAuthPopup();
      if (window.opener && window.opener !== window) {
        window.opener.postMessage({ type: CONNECTOR_OAUTH_MESSAGE_TYPE, payload: response.data }, window.location.origin);
      }
      if (isPopup) {
        handledInPopup = true;
        window.setTimeout(() => window.close(), 50);
        window.setTimeout(() => window.close(), 500);
        return;
      }
      message.success('Connector connected');
      setScreen('dashboard');
    }).catch((error: any) => {
      if (cancelled) return;
      const isPopup =
        isRememberedConnectorOAuthPopup(window.location.search) ||
        (window.opener && window.opener !== window) ||
        window.name.endsWith('_oauth');
      clearUrl();
      const detail = error?.response?.data?.detail ?? error?.message ?? 'Connector sign-in failed';
      broadcastConnectorOAuthResult({ error: detail });
      clearRememberedConnectorOAuthPopup();
      if (window.opener && window.opener !== window) {
        window.opener.postMessage({ type: CONNECTOR_OAUTH_MESSAGE_TYPE, error: detail }, window.location.origin);
      }
      if (isPopup) {
        handledInPopup = true;
        window.setTimeout(() => window.close(), 50);
        window.setTimeout(() => window.close(), 500);
        return;
      }
      message.error(detail);
    }).finally(() => {
      if (!cancelled && !handledInPopup) {
        setHandlingConnectorOAuth(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleLoginSuccess = async (googleCredential: string) => {
    const decoded = decodeGoogleJwt(googleCredential);
    setAuthError('');

    // Register (or accept a beta invite) before persisting the local session.
    // A 403 here means the beta gate intentionally denied access.
    if (decoded.email) {
      try {
        await api.registerUser(decoded.email, decoded.name || decoded.email, getInviteToken());
      } catch (err: any) {
        const detail = err?.response?.data?.detail || err?.message || 'Could not verify beta access';
        if (err?.response?.status === 403) {
          setAuthError(detail);
          message.warning(detail);
          return;
        }
        console.warn('[Praxys] Could not register user on login:', err);
      }
    }

    saveSession(decoded);
    setUser(decoded);
    window.history.replaceState({}, '', '/app');
    setScreen('splash');
  };

  const handleLogout = () => {
    clearSession();
    setUser(null);
    setActiveTaskId(null);
    window.history.replaceState({}, '', '/login');
    setScreen('login');
  };

  if (handlingConnectorOAuth) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0f172a',
          color: '#e2e8f0',
          fontSize: 16,
          fontWeight: 600,
          textAlign: 'center',
          padding: 24,
        }}
      >
        <div>
          <div style={{ marginBottom: 10 }}>Completing connector sign-in...</div>
          <button
            type="button"
            onClick={() => window.close()}
            style={{
              color: '#e6edf3',
              border: 'none',
              borderRadius: 12,
              background: '#1e1e1e',
              boxShadow: '0 10px 28px rgba(56, 189, 248, 0.18)',
              padding: '8px 14px',
              cursor: 'pointer',
            }}
          >
            Close window
          </button>
        </div>
      </div>
    );
  }

  if (window.location.pathname === '/terms') {
    return <TermsOfServiceView variant="public" />;
  }

  if (window.location.pathname === '/privacy') {
    return <PrivacyPolicyView variant="public" />;
  }

  if (window.location.pathname === '/') {
    return <PublicHomePage />;
  }

  if (screen === 'login' || window.location.pathname === '/login' || !user) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} errorMessage={authError} />;
  }

  if (screen === 'splash') {
    return <SplashScreen onDone={() => setScreen('dashboard')} />;
  }

  return (
    <AgentDashboard
      onSelectTask={setActiveTaskId}
      activeTaskId={activeTaskId}
      onClearTask={() => setActiveTaskId(null)}
      user={user}
      onLogout={handleLogout}
      isAdmin={isAdminUser(user)}
      isLightMode={false}
    />
  );
};

export default App;
